import { AgGridModule } from 'ag-grid-angular';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FeatureTogglesModule } from '@cbre/feature-toggles';
import { RuleEngineModule } from '@cbre/rule-engine';
import { TranslationModule } from '@miq/utils';
// tslint:disable:max-line-length
import { AvailabilityAgreementDateComponent } from '@records/controls/custom/common/availablity-agreement-date/availability-agreement-date.component';
import { FilterOptionComponent } from '@records/records-search/filter-set/filter-option/filter-option.component';
import { SaveSearchDialogComponent } from '@records/records-search/save-search/save-search-dialog.component';
import { LastGroupRendererComponent } from '@reports/report-details/corporate/multi-period/last-group-renderer/last-group-renderer.component';
import { ExportLimitsComponent } from '@shared/components/export-limits/export-limits.component';
import { GlobalSelectionComponent } from '@shared/components/global-selection/global-selection.component';
import { LoadingIndicatorComponent } from '@shared/components/loading-indicator/loading-indicator.component';
import { LoadingIndicatorModeDirective } from '@shared/components/loading-indicator/loading-mode.directive';
import { LocationPermissionsComponent } from '@shared/components/location-permissions/location-permissions.component';
import { MatDropdownComponent } from '@shared/components/mat-dropdown/mat-dropdown.component';
import { RecordsPermissionsComponent } from '@shared/components/records-permissions/records-permissions.component';
import { MomentToNowPipe } from '@shared/components/service-tickets/ticket-list/moment-to-now.pipe';
import { TicketListComponent } from '@shared/components/service-tickets/ticket-list/ticket-list.component';
import { SubSelectionComponent } from '@shared/components/sub-selection/sub-selection.component';
import { TooltipComponent, TooltipContainerDirective } from '@shared/components/tooltip/tooltip.component';
import { AddLocationComponent } from '@shared/dialogs/add-location/add-location.component';
import { RecordPermissionDialogComponent } from '@shared/dialogs/record-permission-dialog/record-permission-dialog.component';
import { L10nTranslationModule } from 'angular-l10n';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CalendarModule } from 'primeng/calendar';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TableModule } from 'primeng/table';
import { ChangeTagsComponent } from './bulk-tag-change/change-tags/change-tags.component';
import { TagChangeStepperComponent } from './bulk-tag-change/tag-change-stepper/tag-change-stepper.component';
import { ChartRenderComponent } from './charts/chart-render/chart-render.component';
import { CompareAssociatedRecordsComponent } from './compare-associated-records/compare-associated-records.component';
import { CompareCommonFieldsComponent } from './compare-associated-records/compare-common-fields/compare-common-fields.component';
import { CardComponent } from './components/card/card.component';
import { CollapseAllComponent } from './components/collapse-all/collapse-all.component';
import { CommentsComponent } from './components/comments/comments.component';
import { EntryCommentComponent } from './components/comments/entry-comment/entry-comment.component';
import { ChildTableComponent } from './components/complex-table/child-table/child-table.component';
import { ComplexTableRowComponent } from './components/complex-table/complex-table-row/complex-table-row.component';
import { ComplexTableComponent } from './components/complex-table/complex-table.component';
import { ConsoleComponent } from './components/console/console.component';
import { PrintControlFlagsPipe } from './components/console/print-control-flags.pipe';
import { ContactCardComponent } from './components/contact-card/contact-card.component';
import { CountBadgeComponent } from './components/count-badge/count-badge.component';
import { CreateSelectFoldersComponent } from './components/create-select-folders/create-select-folders.component';
import { CumulativeBoxComponent } from './components/editing-box/cumulative-box/cumulative-box.component';
import { EditingBoxArraysComponent } from './components/editing-box/editing-box-arrays/editing-box-arrays.component';
import { EditingBoxFieldComponent } from './components/editing-box/editing-box-field/editing-box-field.component';
import { EditingBoxTargetDirective } from './components/editing-box/editing-box-target.directive';
import { EditingBoxComponent } from './components/editing-box/editing-box.component';
import { LeaseTermEditBoxComponent } from './components/editing-box/lease-term-edit-box/lease-term-box.component';
import { ErrorPageComponent } from './components/error-page/error-page.component';
import { ErrorsCardComponent } from './components/errors-card/errors-card.component';
import { AutoCompleteComponent } from './components/filters-bar/auto-complete/auto-complete.component';
import { EqualsFilterComponent } from './components/filters-bar/equals-filter/equals-filter.component';
import { FilterBarLabelComponent } from './components/filters-bar/filter-bar-label/filter-bar-label.component';
import { FiltersBarComponent } from './components/filters-bar/filters-bar.component';
import { PeriodRangeComponent } from './components/filters-bar/period-range/period-range.component';
import { SearchBarComponent } from './components/filters-bar/search-bar/search-bar.component';
import { SimpleSelectComponent } from './components/filters-bar/simple-select/simple-select.component';
import { GoBackLinkComponent } from './components/go-back-link/go-back-link.component';
import { GoesToExternalRecordComponent } from './components/goes-to-external-record/goes-to-external-record.component';
import { GoesToRecordComponent } from './components/goes-to-record/goes-to-record.component';
import { InlineMessageComponent } from './components/inline-message/inline-message.component';
import { InputsModule } from './components/inputs/inputs.module';
import { PrivacyContactCardComponent } from './components/privacy-contact-card/privacy-contact-card.component';
import { PrivacyTeamContactCardComponent } from './components/privacy-team-contact-card/privacy-team-contact-card.component';
import { RangeItemComponent } from './components/range-item/range-item.component';
import { SearchTypeaheadComponent } from './components/search/search-typeahead/search-typeahead.component';
import { SearchComponent } from './components/search/search.component';
import { BgImageComponent } from './components/secured-resource/bg-image/bg-image.component';
import { SecuredDownloadLinkComponent } from './components/secured-resource/secured-download-link.component';
import { SecuredLinkComponent } from './components/secured-resource/secured-link.component';
import { SecuredResourceComponent } from './components/secured-resource/secured-resource.component';
import { TicketDetailAdditionalInfoComponent } from './components/service-tickets/ticket-detail/ticket-detail-additional-info/ticket-detail-additional-info.component';
import { AttachmentComponent } from './components/service-tickets/ticket-detail/ticket-detail-attachments/attachment/attachment.component';
import { TicketDetailAttachmentsComponent } from './components/service-tickets/ticket-detail/ticket-detail-attachments/ticket-detail-attachments.component';
import { TicketDetailHeaderComponent } from './components/service-tickets/ticket-detail/ticket-detail-header/ticket-detail-header.component';
import { TicketDetailInfoComponent } from './components/service-tickets/ticket-detail/ticket-detail-info/ticket-detail-info.component';
import { TicketDetailStatusComponent } from './components/service-tickets/ticket-detail/ticket-detail-status/ticket-detail-status.component';
import { TicketDetailComponent } from './components/service-tickets/ticket-detail/ticket-detail.component';
import { TicketRowComponent } from './components/service-tickets/ticket-list/ticket-row/ticket-row.component';
import { TicketMenuComponent } from './components/service-tickets/ticket-menu/ticket-menu.component';
import { TitleDirective } from './components/set-title/title.directive';
import { ContactListComponent } from './components/share-with-contacts/contact-list/contact-list.component';
import { ShareWithContactsComponent } from './components/share-with-contacts/share-with-contacts.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { TablePaginatorComponent } from './components/table-paginator/table-paginator.component';
import { CustomHeaderComponent } from './components/table/ag-header.component';
import { AgTableCellRendererComponent } from './components/table/ag-table-cell-renderer.component';
import { AgTableComponent } from './components/table/ag-table.component';
import { GoToRecordRendererComponent } from './components/table/go-to-record-renderer.component';
import { IconCellRendererComponent } from './components/table/icon-cell-renderer.component';
import { AgDateComponent } from './components/table/inputs/ag-date.component';
import { AgNaicsInputComponent } from './components/table/inputs/ag-naics-input.component';
import { AgNumericComponent } from './components/table/inputs/ag-numeric.component';
import { AgPopupTextComponent } from './components/table/inputs/ag-popup-text.component';
import { AgSelectComponent } from './components/table/inputs/ag-select.component';
import { AgUomComponent } from './components/table/inputs/ag-uom.component';
import { MiqTagsModule } from './components/tags';
import { TeamContactCardComponent } from './components/team-contact-card/team-contact-card.component';
import { TopNotificationComponent } from './components/top-notification/top-notification.component';
import { CommandsComponent } from './components/tree-data-table/commands/commands.component';
import { ContextualizedPipe } from './components/tree-data-table/tree-data-row/contextualized.pipe';
import { IndentDirective } from './components/tree-data-table/tree-data-row/indent.directive';
import { SubtitleCellFormaterPipe } from './components/tree-data-table/tree-data-row/subtitle-cell-formater.pipe';
import { TreeDataRowComponent } from './components/tree-data-table/tree-data-row/tree-data-row.component';
import { TreeDataTableComponent } from './components/tree-data-table/tree-data-table.component';
import { UomRecordDetailInputsComponent } from './components/uom-record-detail-inputs/uom-record-detail-inputs.component';
import { AssignComponent } from './dialogs/assign/assign.component';
import { ConfirmDialogComponent } from './dialogs/confirm-dialog/confirm-dialog.component';
import { EdpEditFailedComponent } from './dialogs/edp-edit-failed/edp-edit-failed.component';
import { ExportlimitAlertComponent } from './dialogs/exportlimit-alert/exportlimit-alert.component';
import { MoreFiltersDialogWrapperComponent } from './dialogs/more-filters/more-filters-dialog-wrapper.component';
import { MoreFiltersComponent } from './dialogs/more-filters/more-filters.component';
import { OutdatedRecordsAlertComponent } from './dialogs/outdated-records-alert/outdated-records-alert.component';
import { PrivateAssetSettingsDialogComponent } from './dialogs/private-asset-settings-dialog/private-asset-settings-dialog.component';
import { SessionExpiryDialogComponent } from './dialogs/session-expiry-dialog/session-expiry-dialog.component';
import { TicketFilterComponent } from './dialogs/ticket-filter/ticket-filter.component';
import { DirectivesModule } from './directives/directives.module';
import { SingleFilterBarComponent } from './filter-bar/miq-single-filter-bar.component';
import { InternalSingleFilterBarComponent } from './filter-bar/single-filter-bar.component';
import { SharedPipesModule } from './pipe/shared-pipes.module';
import { CiqHttpInterceptor } from './services/ciq/ciq-http.interceptor';
import { DeviceInfoService } from './services/device-info.service';
import { DialogService } from './services/dialog.service';
import { LoadingHttpInterceptor } from './services/loading-interceptor.service';
import { ErrorCodesPipe } from './utils/pipes/error-codes.pipe';
import { MiqTableCheckboxComponent } from './vendors/table-checkbox.component';
import { MiqTableHeaderCheckboxComponent } from './vendors/table-header-checkbox.component';
import { MiqTableRadioButtonComponent } from './vendors/table-radiobutton.component';
import { WizardProjectorDirective } from './wizard/wizard-projector.directive';
import { WizardComponent } from './wizard/wizard.component';
import { WizardSlotSelectionPipe } from './wizard/wizard-slot-selection.pipe';
import { SelectWithSearchComponent } from './components/select-with-search/select-with-search.component';
import { MiqTrackUserActionsDirective } from './track-user-actions/track-user-actions.directive';
import { MiqTrackUserActionInterceptor } from './track-user-actions/track-user-actions-interceptor.service';
import { FilterNaicsComponent } from './components/filters-bar/filter-naics/filter-naics.component';
import { AlertListRecordsComponent } from './components/alert-list-records/alert-list-records.component';
import { MiqTrackUserActionsComponent } from './track-user-actions/track-user-actions.component';
import { ButtonTabsComponent } from './button-tabs/button-tabs.component';
import { AuditHistoryApiService } from './services/audit-history-api.service';
import { SelectionTableComponent } from './components/selection-table/selection-table.component';
import { SelectionTableDialogComponent } from './dialogs/selection-table-dialog/selection-table-dialog.component';
import { SimpleInlineMessageComponent } from './components/simple-inline-message/simple-inline-message.component';
import { SpinnerLoadingIndicatorComponent } from './components/spinner-loading-indicator/spinner-loading-indicator.component';
import { KpiSummaryComponent } from './components/kpi-summary/kpi-summary.component';
import { SectionOptionsComponent } from './dialogs/more-filters/section-options.component';
import { SectionOptionsFilterComponent } from './dialogs/more-filters/section-options-filter.component';
import { LeaseRecordTypeComponent } from './components/lease-record-type/lease-record-type.component';
import {
  DefaultMoreFiltersCustomOptionsService,
  MORE_FILTERS_CUSTOM_OPTIONS_TOKEN,
} from './dialogs/more-filters/more-filters-custom-options.service';
import { CHECKBOX_SERVICE_TOKEN, CheckboxService } from './services/checkbox-state.service';
import {
  INTERNAL_SINGLE_FILTER_BAR_GUARD_TOKEN,
  InternalSingleFilterBarGuard,
} from './filter-bar/internal-single-filter-bar.guard';
import { PropertySetConfirmDialogComponent } from './dialogs/property-set-confirm-dialog/property-set-confirm-dialog.component';
import { AgPropertySetCellRendererComponent } from './components/table/property-set-cell-renderer/ag-property-set-cell-renderer.component';
import { PropertySetColumnComponent } from '@miq/property-sets';
import { CanUpdateComplextRowPipe } from '@shared/components/complex-table/complex-table-row/canupdate-row.pipe';
import { ClipboardMoreFiltersDialogWrapperComponent } from './dialogs/more-filters/clipboard-more-filters-dialog-wrapper.component';
import { AgYearComponent } from './components/table/inputs/ag-year/ag-year.component';
import { BrokerFeedbackDialogComponent } from './components/broker-feedback/broker-feedback-dialog.component';
import { FilterUserGroupsPipe } from './pipe/filter-user-group.pipe';
import { ImagePreviewCellRendererComponent } from './components/table/image-preview-cell-renderer/image-preview-cell-renderer.component';
import { ImagePreviewDialogComponent } from './components/image-preview-dialog/image-preview-dialog.component';
import { QuarterSelectorModule } from '@cbre/components';
import { CompoundFilterSelectionComponent } from '@cbre/components';
import { GoBackLinkDirective } from './components/go-back-link/go-back-link.directive';
import { TimePeriodValidationCellRendererComponent } from './components/table/time-period-validation-renderer.component';
import { EdpRecordsUpdateService } from './services/edp-records-update.service';

@NgModule({
  exports: [
    TranslationModule,
    InputsModule,
    DirectivesModule,
    SharedPipesModule,
    AgGridModule,
    NgxSpinnerModule,
    GlobalSelectionComponent,
    SubSelectionComponent,
    AddLocationComponent,
    MatDropdownComponent,
    RecordPermissionDialogComponent,
    ConfirmDialogComponent,
    LocationPermissionsComponent,
    RecordsPermissionsComponent,
    ExportLimitsComponent,
    NgxPermissionsModule,
    TicketListComponent,
    TicketDetailComponent,
    MoreFiltersComponent,
    MoreFiltersDialogWrapperComponent,
    ClipboardMoreFiltersDialogWrapperComponent,
    TablePaginatorComponent,
    TicketDetailHeaderComponent,
    TicketDetailAdditionalInfoComponent,
    TicketDetailAttachmentsComponent,
    TicketDetailInfoComponent,
    TicketDetailStatusComponent,
    TopNotificationComponent,
    CommentsComponent,
    FiltersBarComponent,
    SimpleSelectComponent,
    GoBackLinkComponent,
    TreeDataTableComponent,
    CommandsComponent,
    TreeDataRowComponent,
    ContextualizedPipe,
    SubtitleCellFormaterPipe,
    IndentDirective,
    EdpEditFailedComponent,
    ContactCardComponent,
    PrivacyContactCardComponent,
    PrivacyTeamContactCardComponent,
    MiqTableCheckboxComponent,
    MiqTableRadioButtonComponent,
    MiqTableHeaderCheckboxComponent,
    CreateSelectFoldersComponent,
    SecuredResourceComponent,
    SecuredLinkComponent,
    SecuredDownloadLinkComponent,
    BgImageComponent,
    SearchComponent,
    LoadingIndicatorComponent,
    LoadingIndicatorModeDirective,
    EditingBoxTargetDirective,
    CardComponent,
    FeatureTogglesModule,
    ErrorPageComponent,
    EditingBoxFieldComponent,
    UomRecordDetailInputsComponent,
    GoesToRecordComponent,
    GoesToExternalRecordComponent,
    LeaseRecordTypeComponent,
    TitleDirective,
    AgTableComponent,
    ChildTableComponent,
    ComplexTableRowComponent,
    ComplexTableComponent,
    CountBadgeComponent,
    L10nTranslationModule,
    ChartRenderComponent,
    SingleFilterBarComponent,
    MiqTagsModule,
    RangeItemComponent,
    ErrorsCardComponent,
    SearchTypeaheadComponent,
    ConsoleComponent,
    PrintControlFlagsPipe,
    FilterOptionComponent,
    StepperComponent,
    ErrorCodesPipe,
    CollapseAllComponent,
    SearchBarComponent,
    ShareWithContactsComponent,
    ContactListComponent,
    InlineMessageComponent,
    MiqTrackUserActionsDirective,
    AlertListRecordsComponent,
    MiqTrackUserActionsComponent,
    ButtonTabsComponent,
    InternalSingleFilterBarComponent,
    SelectionTableComponent,
    SelectionTableDialogComponent,
    SimpleInlineMessageComponent,
    SpinnerLoadingIndicatorComponent,
    KpiSummaryComponent,
    CanUpdateComplextRowPipe,
    BrokerFeedbackDialogComponent,
    FilterUserGroupsPipe,
    ImagePreviewDialogComponent,
    GoBackLinkDirective,
  ],
  declarations: [
    AvailabilityAgreementDateComponent,
    FilterOptionComponent,
    SingleFilterBarComponent,
    InternalSingleFilterBarComponent,
    GlobalSelectionComponent,
    SubSelectionComponent,
    AddLocationComponent,
    MatDropdownComponent,
    RecordPermissionDialogComponent,
    ConfirmDialogComponent,
    LocationPermissionsComponent,
    RecordsPermissionsComponent,
    ExportLimitsComponent,
    TooltipComponent,
    TooltipContainerDirective,
    SessionExpiryDialogComponent,
    TicketListComponent,
    MomentToNowPipe,
    TicketFilterComponent,
    TicketRowComponent,
    TicketDetailComponent,
    TicketMenuComponent,
    AssignComponent,
    MoreFiltersComponent,
    MoreFiltersDialogWrapperComponent,
    ClipboardMoreFiltersDialogWrapperComponent,
    TablePaginatorComponent,
    TicketDetailHeaderComponent,
    TicketDetailAdditionalInfoComponent,
    TicketDetailAttachmentsComponent,
    TicketDetailInfoComponent,
    TicketDetailStatusComponent,
    TopNotificationComponent,
    CommentsComponent,
    FiltersBarComponent,
    FiltersBarComponent,
    SearchBarComponent,
    SimpleSelectComponent,
    GoBackLinkComponent,
    GoBackLinkDirective,
    TreeDataTableComponent,
    TreeDataRowComponent,
    ContextualizedPipe,
    SubtitleCellFormaterPipe,
    IndentDirective,
    CommandsComponent,
    EdpEditFailedComponent,
    OutdatedRecordsAlertComponent,
    ExportlimitAlertComponent,
    ContactCardComponent,
    PrivacyContactCardComponent,
    PrivacyTeamContactCardComponent,
    MiqTableCheckboxComponent,
    MiqTableRadioButtonComponent,
    MiqTableHeaderCheckboxComponent,
    CreateSelectFoldersComponent,
    SecuredResourceComponent,
    SecuredLinkComponent,
    SecuredDownloadLinkComponent,
    AttachmentComponent,
    BgImageComponent,
    EntryCommentComponent,
    SearchComponent,
    LoadingIndicatorComponent,
    LoadingIndicatorModeDirective,
    UomRecordDetailInputsComponent,
    EditingBoxComponent,
    LeaseTermEditBoxComponent,
    EditingBoxFieldComponent,
    EditingBoxTargetDirective,
    CardComponent,
    ErrorPageComponent,
    EditingBoxArraysComponent,
    SaveSearchDialogComponent,
    GoesToRecordComponent,
    LeaseRecordTypeComponent,
    TitleDirective,
    AgTableCellRendererComponent,
    AgPropertySetCellRendererComponent,
    AgTableComponent,
    AgNumericComponent,
    IconCellRendererComponent,
    CustomHeaderComponent,
    GoToRecordRendererComponent,
    AgSelectComponent,
    AgDateComponent,
    AgNaicsInputComponent,
    AgPopupTextComponent,
    AgUomComponent,
    ChildTableComponent,
    ComplexTableRowComponent,
    ComplexTableComponent,
    CountBadgeComponent,
    ChartRenderComponent,
    PeriodRangeComponent,
    FilterBarLabelComponent,
    WizardComponent,
    WizardProjectorDirective,
    WizardSlotSelectionPipe,
    CumulativeBoxComponent,
    AutoCompleteComponent,
    ChangeTagsComponent,
    TagChangeStepperComponent,
    StepperComponent,
    RangeItemComponent,
    ErrorsCardComponent,
    SearchTypeaheadComponent,
    CompareAssociatedRecordsComponent,
    CompareCommonFieldsComponent,
    ConsoleComponent,
    PrintControlFlagsPipe,
    PrivateAssetSettingsDialogComponent,
    TeamContactCardComponent,
    LastGroupRendererComponent,
    ErrorCodesPipe,
    CollapseAllComponent,
    EqualsFilterComponent,
    ShareWithContactsComponent,
    ContactListComponent,
    InlineMessageComponent,
    SelectWithSearchComponent,
    MiqTrackUserActionsDirective,
    FilterNaicsComponent,
    AlertListRecordsComponent,
    MiqTrackUserActionsComponent,
    ButtonTabsComponent,
    SelectionTableComponent,
    SelectionTableDialogComponent,
    KpiSummaryComponent,
    PropertySetConfirmDialogComponent,
    CanUpdateComplextRowPipe,
    AgYearComponent,
    BrokerFeedbackDialogComponent,
    FilterUserGroupsPipe,
    ImagePreviewCellRendererComponent,
    ImagePreviewDialogComponent,
    TimePeriodValidationCellRendererComponent,
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgxPermissionsModule.forRoot(),
    TableModule,
    OverlayPanelModule,
    FeatureTogglesModule.forChild(),
    L10nTranslationModule,
    TranslationModule,
    CalendarModule,
    AgGridModule,
    NgxSpinnerModule,
    MiqTagsModule,
    QuarterSelectorModule,
    DirectivesModule,
    InputsModule,
    SharedPipesModule,
    RuleEngineModule,
    SimpleInlineMessageComponent,
    SpinnerLoadingIndicatorComponent,
    SectionOptionsComponent,
    SectionOptionsFilterComponent,
    PropertySetColumnComponent,
    CompoundFilterSelectionComponent,
    GoesToExternalRecordComponent,
  ],
  providers: [
    DialogService,
    DeviceInfoService,
    AuditHistoryApiService,
    CanUpdateComplextRowPipe,
    EdpRecordsUpdateService,
    { provide: HTTP_INTERCEPTORS, useClass: CiqHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MiqTrackUserActionInterceptor, multi: true },
    { provide: MORE_FILTERS_CUSTOM_OPTIONS_TOKEN, useClass: DefaultMoreFiltersCustomOptionsService },
    { provide: CHECKBOX_SERVICE_TOKEN, useClass: CheckboxService },
    { provide: INTERNAL_SINGLE_FILTER_BAR_GUARD_TOKEN, useClass: InternalSingleFilterBarGuard },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class SharedModule {}
